import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { SectionHeader } from "@/components/common/Widgets";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#1F2A44", "#0F766E", "#0EA5E9", "#16A34A", "#D97706", "#DC2626", "#9333EA", "#475569", "#0891B2", "#A16207", "#7C3AED"];

export default function ReportsPage() {
  const [pipeline, setPipeline] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [byType, setByType] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, k, apps] = await Promise.all([
        api.get("/dashboard/pipeline"),
        api.get("/dashboard/kpis"),
        api.get("/applications"),
      ]);
      setPipeline(p.data);
      setKpis(k.data);
      const counts = {};
      apps.data.forEach(a => { counts[a.loan_type] = (counts[a.loan_type] || 0) + 1; });
      setByType(Object.entries(counts).map(([type, count]) => ({ type, count })));
    })();
  }, []);

  return (
    <div className="space-y-5">
      <SectionHeader title="Reports & MIS" subtitle="Pipeline distribution, type-wise breakup, and KPIs." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold heading mb-2">Pipeline by Stage</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline.map(p => ({ stage: p.stage.replace(/_/g, ' '), count: p.count }))} margin={{ left: 10, right: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="stage" angle={-30} textAnchor="end" interval={0} fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(221 55% 22%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-semibold heading mb-2">By Loan Type</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byType} dataKey="count" nameKey="type" outerRadius={100} label>
                  {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      {kpis && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold heading mb-3">Portfolio Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV k="Total Applications" v={kpis.total_applications} />
            <KV k="In Progress" v={kpis.in_progress} />
            <KV k="Sanctioned" v={kpis.sanctioned} />
            <KV k="Rejected" v={kpis.rejected} />
            <KV k="Pending KYC" v={kpis.pending_kyc} />
            <KV k="In Credit Review" v={kpis.in_credit} />
            <KV k="Awaiting Approval" v={kpis.in_approval} />
            <KV k="Total Customers" v={kpis.customers} />
          </div>
        </Card>
      )}
    </div>
  );
}
function KV({ k, v }) {
  return (<div className="rounded-md border p-3 bg-muted/30"><div className="text-[11px] uppercase text-muted-foreground font-medium">{k}</div><div className="mt-0.5 text-xl mono font-semibold tabular">{v ?? "—"}</div></div>);
}
